import { Diagnostics } from "@base/diagnostics/Diagnostics";
import { DbHelper } from "@base/util/DbHelper";
import { IdGen } from "@base/util/IdGen";
import { ImmHelper } from "@base/util/Immutable";
import { NormalizedRecordUtil } from "@base/util/NormalizedRecord";
import { getKeys, mapRecord } from "@base/util/TypeUtil";
import { Debug } from "@biz/Debug";
import { SceneType } from "@biz/Definitions";
import { DealerType } from "@biz/definitions/AccessDefinitions";
import { FeatureLevel } from "@biz/definitions/ProductDefinitions";
import { IEdgeData, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { VariantData3d } from "@biz/definitions/VariantData3d";
import { ISail2dForm } from "@biz/models/Sail2dDefinitions";
import { IShoppingListItem } from "@biz/models/ShoppingListDefinitions";
import { ILockedByInfo, IServerVariant, IServerVariantSummary, IUploadHistory, IUserFileInfo, IVariant, IVariantData2d, VARIANT_VERSION, VariantDataType, VariantDataTypes } from "@biz/userProjects/new/IVariant";
import { ProjectPreview } from "@biz/userProjects/new/ProjectPreview";
import { VariantData2d } from "@biz/userProjects/new/VariantData2d";
import { VariantDataShop } from "@biz/userProjects/new/VariantDataShop";

export class Variant {
  static createVariantDataForType(type: VariantDataTypes, featureLevel: FeatureLevel, dealerType: DealerType | undefined): VariantDataType {
    switch (type) {
      case "sail2d":
        return VariantData2d.createDefault(featureLevel, dealerType);
      case "sail3d":
        return VariantData3d.create();
      case "shop":
        return VariantDataShop.create();

    }
    Debug.assert(false, "Type not supported so far!");
  }

  static createForProject(type: VariantDataTypes, projectId: string, owner: string, ownerCompanyId: number | undefined, featureLevel: FeatureLevel, dealerType: DealerType | undefined): IVariant {
    return {
      _id: DbHelper.createId(),
      variantName: "Variante",
      owner,
      ownerCompanyId,
      projectId,
      token: IdGen.unsecure.get(),
      version: VARIANT_VERSION,
      versionConfirmed: { version: VARIANT_VERSION, timestamp: Date.now() },
      data: Variant.createVariantDataForType(type, featureLevel, dealerType)
    };
  }

  static isOutdated(variant: { version: number }): boolean {
    return variant.version < VARIANT_VERSION;
  }

  static isReadOnly(variant: IVariant | undefined, force?: boolean): boolean {
    if (force)
      return true;
    const sceneType = variant ? Variant.calcSceneType(variant) : "scene";
    return sceneType !== "scene";
  }

  static isReadOnlyFromUploadHistory(uploadHistory: IUploadHistory[] | undefined, force?: boolean): boolean {
    if (force)
      return true;
    const sceneType = Variant.calcSceneTypeFromUploadHistory(uploadHistory);
    return sceneType !== "scene";
  }

  static isLocked(variant: IVariant | undefined, loggedInUserId: string | undefined, force?: boolean): boolean {
    return force || (!!variant?.lockedBy && variant.lockedBy.userId !== loggedInUserId);
  }

  static isLockedFromLockedBy(lockedBy: ILockedByInfo | undefined, loggedInUserId: string | undefined, force?: boolean): boolean {
    return force || (!!lockedBy && lockedBy.userId !== loggedInUserId);
  }

  static upgradeCompanyId<T extends IVariant>(variant: T, user?: { _id: string, companyId?: number }): T {
    if (user?.companyId && variant.owner === user._id) {
      variant = ImmHelper.setProperty(variant, "ownerCompanyId", user.companyId)
    }
    return variant;
  }

  static upgradeShoppingIdPoleIndexToPoleId(item: IShoppingListItem): IShoppingListItem {
    return item;
  }

  static upgradeIfOutdated<T extends IVariant>(variant: T): T {
    if (this.isOutdated(variant)) {
      try {
        console.warn("Outdated! ", variant.version);
        const v = Variant.#upgrade(variant)
        console.warn("Upated!", v.version);
        return v;
      } catch (e) {
        Diagnostics.log(e, "Failed to upgrade");
      }
    }
    return variant;
  }

  static #upgrade<T extends IVariant>(variant: T): T {
    variant = upgradeUserFiles(variant);

    if (variant.data.type === "sail3d" && !("sideTab" in variant)) {
      const newData = ImmHelper.setProperty(variant.data, "sideTab", "properties")
      variant = ImmHelper.setProperty(variant, "data", newData)
    }

    // upgrade fences
    if (variant.data.type === "sail3d" && !variant.data.fences) {
      const newData = ImmHelper.setProperty(variant.data, "fences", NormalizedRecordUtil.createEmpty());
      variant = ImmHelper.setProperty(variant, "data", newData)
    }

    // upgrade curtains
    if (variant.data.type === "sail3d" && !variant.data.curtains) {
      const newData = ImmHelper.setProperty(variant.data, "curtains", NormalizedRecordUtil.createEmpty());
      variant = ImmHelper.setProperty(variant, "data", newData)
    }

    const vd = variant.data;
    if (vd.type === "sail3d" || vd.type === "sail2d") {
      const sunSails = vd.sunSails;
      if (sunSails?.byId) {
        for (const key of getKeys(sunSails.byId)) {
          let ssd = sunSails.byId[key];
          if (ssd?.edges) {
            for (const en of getKeys(ssd.edges)) {
              const edge = ssd.edges![en];
              if (!edge?.seamType) {
                if ((edge as any).hasHemstitch) {
                  const newEdge: IEdgeData = {
                    ...edge,
                    seamType: "hemstitch"
                  }
                  delete (newEdge as any).hasHemstitch;
                  ssd = ImmHelper.setPropertyNonNullable(ssd, "edges", en, newEdge);
                } else if (edge?.kederSize) {
                  const newEdge: IEdgeData = {
                    ...edge,
                    seamType: "keder",
                    concavity: "straight",
                    kederSelectionPriority: 1
                  }
                  ssd = ImmHelper.setPropertyNonNullable(ssd, "edges", en, newEdge);
                }
              }
            }
          }
          const newData = ImmHelper.setPropertyNonNullable(vd, "sunSails", "byId", key, ssd);
          variant = ImmHelper.setProperty(variant, "data", newData)
        }
      }
    }

    let oldData = variant.data;
    if (oldData.type === "sail2d") {
      const ssids = Object.keys(oldData.sunSails.byId);
      for (const id of ssids) {
        let fixed: ISail2dForm = oldData.sunSails.byId[id];
        if (fixed.sunSailType === SunSailType.SolidayCS || fixed.sunSailType === SunSailType.SolidayXS) {
          if (!fixed.outC && (fixed as any).distOut) {
            fixed = ImmHelper.setProperty(fixed, "outC", (fixed as any).distOut);
          }
        }

        oldData = updateSunSail2d(oldData, fixed)
      }
    } else if (oldData.type === "sail3d") {
      if (oldData.sunSails.byId) {
        // const sunSailIds = Object.keys(oldData.sunSails.byId);
        // for (const k of sunSailIds) {
        // const ss = oldData.sunSails.byId[k];

        // if (ss.concavities) {
        //   const edges = mapRecord(ss.concavities, (concavity) => {
        //     const edge: IEdgeData = {
        //       concavity,
        //       confection: "default"
        //     }
        //     return edge;
        //   });
        //   let newSS = ImmHelper.setProperty(ss, "edges", edges);
        //   newSS = ImmHelper.deleteProperty(ss, "concavities") as ISunSailData;
        //   oldData = updateSunSail3d(oldData, newSS);
        // }
        // }
      }
    }
    variant = ImmHelper.setProperty(variant, "data", oldData);
    variant = ImmHelper.setProperty(variant, "version", VARIANT_VERSION);

    return variant;
  }

  static clone<T extends IVariant>(variant: T, newId: string, newName: string, newOwner: string, newOwnerCompanyId: number | undefined/*, newPreview: IProjectPreviewNew | undefined*/): T {
    const cloneHistory = [
      ...(variant.cloneHistory || []),
      {
        date: Date.now(),
        sourceId: variant._id,
        ownerId: variant.owner,
        companyId: variant.ownerCompanyId
      },
    ];

    const newVariant: T = {
      ...variant,
      _id: newId,
      variantName: newName,
      version: VARIANT_VERSION,
      owner: newOwner,
      ownerCompanyId: newOwnerCompanyId,
      // preview: (variant.preview || newPreview) ? (ProjectPreview.asNew(newPreview) || ProjectPreview.create()) : undefined,
      token: IdGen.unsecure.get(),
      cloneHistory,
      uploadHistory: [],
      lockedBy: undefined,
    };

    return newVariant
  }

  static toSummary(item: IServerVariant): IServerVariantSummary {
    const summary: IServerVariantSummary = {
      _id: item._id,
      type: item.data.type,
      token: item.token,
      // version: item.version,
      projectId: item.projectId,
      // owner: item.owner,
      // ownerCompanyId: item.ownerCompanyId,
      variantName: item.variantName,
      preview: ProjectPreview.asNew(item.preview),
      server: item.server,
      uploadHistory: item.uploadHistory,
      lockedBy: item.lockedBy,
      userFileIds: Object.keys(item.data.userFiles || {})
    };
    return summary;
  }

  static calcSceneType(variant: { uploadHistory?: IUploadHistory[] }): SceneType {
    if (!variant.uploadHistory) return "scene";
    let type: SceneType = "scene";
    for (let i = variant.uploadHistory.length - 1; i >= 0; i--) {
      switch (variant.uploadHistory[i].type) {
        case "order":
          return "order";
        case "offer":
          type = "offer";
          break;
      }
    }
    return type;
  }

  static calcSceneTypeFromUploadHistory(uploadHistory?: IUploadHistory[]): SceneType {
    if (!uploadHistory) return "scene";
    let type: SceneType = "scene";
    for (let i = uploadHistory.length - 1; i >= 0; i--) {
      switch (uploadHistory[i].type) {
        case "order":
          return "order";
        case "offer":
          type = "offer";
          break;
      }
    }
    return type;
  }

  static calcSceneTypeMap(variant: { uploadHistory?: IUploadHistory[] }): Record<string, string> {
    const map: Record<string, string> = {};
    this.updateSceneTypeMap(map, variant.uploadHistory);
    return map;
  }

  static updateSceneTypeMap(map: Record<string, string>, uploadHistory?: IUploadHistory[]): Record<string, string> {
    if (!uploadHistory) return map;
    for (let i = uploadHistory.length - 1; i >= 0; i--) {
      const u = uploadHistory[i];
      if (!map[u.type]) map[u.type] = u.sceneId;
    }
    return map;
  }

  static calcMaxSceneType(a: SceneType, b: SceneType): SceneType {
    if (a === "order" || b === "order") return "order";
    if (a === "offer" || b === "offer") return "offer";
    return "scene";
  }
}

function upgradeUserFiles<T extends IVariant>(variant: T): T {
  // upgrade old userFiles
  if (variant.data.userFiles) {
    const keys = Object.keys(variant.data.userFiles);
    if (keys.length > 0 && typeof variant.data.userFiles[keys[0]] === "string") {
      const newUserFiles = mapRecord(variant.data.userFiles, (f) => {
        const id = f as any as string;
        const newF: IUserFileInfo = {
          id,
          type: "texture",
          mimeType: "image/webp",
          name: "image_" + id,
          token: IdGen.unsecure.get(),
          imageSize: f.imageSize
        }
        return newF
      })
      variant = ImmHelper.setProperty(variant, "data", "userFiles", newUserFiles)
    }
  }
  return variant;
}

function updateSunSail2d(old: IVariantData2d, sunSail: ISail2dForm): IVariantData2d {
  return ImmHelper.setProperty(old, "sunSails", "byId", sunSail.id, sunSail);
}
